/**
 * ResourceCard — directory org result card (Block 6.F.2 → X.6.5B design
 * refresh reskin; TypeScript per UX-13 S2, the canonical dense card).
 *
 * The Phase 1 §3 grammar, blessed as-is: identity zone (44px IdentityChip,
 * serif 16px name with the Recommended badge inline, 12.5px ink-soft meta
 * line), tag zone at mt-auto (labeled rows + "+N" counters), hairline
 * footer (phone left, "View →" right with the arrow nudge). Craft
 * corrections applied per §3: row labels rise 10px → 11px (the craft
 * floor; sage-400 stays — 4.86:1 on paper), and the tag zone's 10px
 * wrapper size is gone (chips set their own 11px). Chip variants adopt
 * the S0 semantic names (affirm/caution/memory; facts read neutral) —
 * identical rendered classes, no pixel change.
 *
 * The data logic is UNCHANGED: Services / Specialties / Features
 * (Medicaid / Memory Care / Delivery method / Restrictions) chips and the
 * Recommended badge all render exactly as before.
 */

import { type ReactNode } from 'react'
import { ArrowRight, Phone, MapPin, Lock } from 'lucide-react'
import { Chip, type ChipVariant } from '@/components/primitives/Chip'
import { CategoryChip } from './CategoryTiles'
import { CATEGORY_SLUG_TO_DISPLAY } from '@/lib/uiCategoryLabels'
import { formatCountyDisplay } from '@/lib/serviceAreas'
import { formatPhoneDisplay } from '@/lib/format'
import { RecommendedBadge } from '@/components/RecommendedBadge'
import type { ProviderForSearch } from '@/lib/types'

const MAX_TAGS = 2

/** Medicaid → Chip variant (same mapping as OrganizationDetail's A5 section). */
function medicaidChipVariant(value: string): ChipVariant {
  if (value === 'Yes · Straight') return 'affirm'
  if (value === 'No') return 'neutral'
  return 'caution'
}

const STATE_CODE_DISPLAY: Record<string, string> = {
  NJ: 'NJ', NY: 'NY', PA: 'PA', MA: 'MA', IL: 'IL', FL: 'FL', MD: 'MD',
  CT: 'CT', KY: 'KY', IN: 'IN', IA: 'IA', VA: 'VA', CA: 'CA', DE: 'DE',
}

export interface ResourceCardProps {
  resource: ProviderForSearch
  onClick: (resource: ProviderForSearch) => void
}

export function ResourceCard({ resource, onClick }: ResourceCardProps): ReactNode {
  const {
    name, category_slug, phone, counties, state_coverage,
    subcategories, specialties,
    medicaid, dementia_unit, service_delivery_models, gender_restrictions,
    is_recommended, recommended_at, recommended_by_display_name,
  } = resource

  const categoryDisplay = CATEGORY_SLUG_TO_DISPLAY[category_slug] ?? category_slug

  const subDisplays = subcategories
    .map((s) => s.display_name)
    .sort((a, b) => a.localeCompare(b))
  const specDisplays = specialties
    .map((s) => s.display_name)
    .sort((a, b) => a.localeCompare(b))

  const extraSubCount = Math.max(0, subDisplays.length - MAX_TAGS)
  const extraSpecCount = Math.max(0, specDisplays.length - MAX_TAGS)

  const deliveryModels = service_delivery_models ?? []
  const hasMedicaid = Boolean(medicaid)
  const hasDementia = dementia_unit === true
  const hasDelivery = deliveryModels.length > 0
  const hasGender = Boolean(gender_restrictions)
  const hasFeatures = hasMedicaid || hasDementia || hasDelivery || hasGender

  const locationText = (state_coverage?.length ?? 0) > 0
    ? state_coverage.map((sc) => STATE_CODE_DISPLAY[sc] ?? sc).join(', ')
    : counties.length > 0
      ? counties.map((c) => formatCountyDisplay(c)).join(', ')
      : null

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(resource)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(resource) } }}
      className="group flex min-h-[180px] cursor-pointer flex-col rounded-card bg-paper p-5 shadow-sh2 transition-[box-shadow,transform] duration-[var(--dur-slow)] ease-quiet hover:-translate-y-[3px] hover:shadow-sh3 motion-reduce:transition-none motion-reduce:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
    >
      {/* Header: category chip + name + location */}
      {/* The row wraps: when the card is too narrow for the chip and an
          8rem column side by side, the name column sits under the chip,
          and a word wider than the column breaks before it escapes. */}
      <div className="flex flex-wrap items-start gap-3">
        <CategoryChip display={categoryDisplay} size={44} />
        <div className="min-w-0 grow basis-32">
          <h3 className="flex flex-wrap items-center gap-2 font-display text-[16px] font-semibold leading-snug tracking-[-0.01em] text-sage-800">
            <span className="min-w-0 break-words">{name}</span>
            <RecommendedBadge
              isRecommended={is_recommended}
              recommendedAt={recommended_at}
              recommendedByName={recommended_by_display_name}
            />
          </h3>
          <div className="mt-1.5 flex items-start gap-1.5 text-[12.5px] text-ink-soft">
            <MapPin size={13} aria-hidden="true" className="mt-px shrink-0 text-sage-300" />
            <span>
              {locationText && <span>{locationText}</span>}
              {locationText && ' · '}
              <span>{categoryDisplay}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Tag zone (mt-auto so short cards stay aligned). UX-13 §3: labels at
          the 11px craft floor; no 10px wrapper size (chips set their own). */}
      <div className="mt-auto pt-3">
        {(subDisplays.length > 0 || specDisplays.length > 0 || hasFeatures) && (
          <div className="space-y-1">
            {subDisplays.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-[11px] font-semibold text-sage-400">Services:</span>
                {subDisplays.slice(0, MAX_TAGS).map((t) => (
                  <Chip key={t} variant="service">{t}</Chip>
                ))}
                {extraSubCount > 0 && (
                  <span className="text-[11px] font-medium text-sage-400">+{extraSubCount}</span>
                )}
              </div>
            )}
            {specDisplays.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-[11px] font-semibold text-sage-400">Specialties:</span>
                {specDisplays.slice(0, MAX_TAGS).map((t) => (
                  <Chip key={t} variant="specialty">{t}</Chip>
                ))}
                {extraSpecCount > 0 && (
                  <span className="text-[11px] font-medium text-sage-400">+{extraSpecCount}</span>
                )}
              </div>
            )}
            {hasFeatures && (
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-[11px] font-semibold text-sage-400">Features:</span>
                {hasMedicaid && (
                  <Chip variant={medicaidChipVariant(medicaid!)}>Medicaid: {medicaid}</Chip>
                )}
                {hasDementia && (
                  <Chip variant="memory" data-testid="feature-memory-care">
                    <span className="inline-flex items-center gap-1">
                      <Lock size={11} aria-hidden="true" />Memory Care
                    </span>
                  </Chip>
                )}
                {hasDelivery &&
                  deliveryModels.map((m) => (
                    <Chip key={m} variant="neutral">{m}</Chip>
                  ))}
                {hasGender && <Chip variant="neutral">{gender_restrictions}</Chip>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer: phone + View */}
      <div className="mt-4 flex items-center justify-between border-t border-sage-800/[6%] pt-3">
        {phone ? (
          <span data-testid="resource-phone" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-sage-600">
            <Phone size={14} aria-hidden="true" />
            {formatPhoneDisplay(phone)}
          </span>
        ) : <span />}
        <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-sage-800">
          View
          <ArrowRight size={15} aria-hidden="true" className="transition-transform duration-[var(--dur-base)] ease-quiet group-hover:translate-x-0.5" />
        </span>
      </div>
    </div>
  )
}
